import {
  Amount,
  BigIntMath,
  Fraction,
  Percent,
  Price,
  SupportedChainId,
  Token,
  USD_TOKEN,
} from "@eulerxyz/core-sdk"
import { createSelector } from "@reduxjs/toolkit"
import { Address, formatUnits, getAddress, parseUnits, zeroAddress } from "viem"
import { getEntities, getVaultLabels } from "../../actions/getLabels"
import {
  GetGenericVaultData,
  isClassicVault,
  isManagedVault,
} from "../../hooks"
import { GetVaultData } from "../../query"
import { LTV_DEFAULT_CALCULATION_SCALE } from "../../selectors"
import { FormattedAssetValue, FormattedValue } from "../types"
import {
  Asset,
  Collateral,
  ExperienceMarket,
  GetRewardVaultInfo,
  Reward,
  Vault,
  VaultType,
} from "./types"

const UINT256_MAX = BigInt(2n ** 256n - 1n)
export const selectUnitOfAccount = createSelector(
  [(vault: NonNullable<GetGenericVaultData>) => vault],
  (vault): Asset => {
    let address: Address = getAddress(zeroAddress)
    let decimals = 0n
    if (isClassicVault(vault)) {
      address = getAddress(
        vault.unitOfAccount === zeroAddress
          ? vault.backupAssetPriceInfo.unitOfAccount
          : vault.unitOfAccount,
      )
      decimals = vault.unitOfAccountDecimals
    } else if (isManagedVault(vault)) {
      address = zeroAddress // No unit of account for managed vaults
      decimals = vault.assetDecimals
    }

    return {
      address,
      decimals,
      symbol: vault.unitOfAccountSymbol,
      name: vault.unitOfAccountName,
    }
  },
)

export const selectAsset = createSelector(
  [(vault: NonNullable<GetVaultData>) => vault],
  (vault): Asset => {
    return {
      address: vault.asset,
      decimals: vault.assetDecimals,
      symbol: vault.assetSymbol,
      name: vault.assetName,
    }
  },
)

export const selectUtilization = createSelector(
  [(vault?: GetVaultData) => vault],
  (vault): FormattedValue => {
    if (!vault?.totalAssets || vault?.totalBorrowed)
      return { raw: 0n, scaled: 0 }

    const totalAssets = new Fraction(vault.totalAssets)
    const totalBorrow = new Fraction(vault.totalBorrowed)

    if (totalBorrow.greaterThan(0) && totalAssets.greaterThan(0)) {
      const utilisation = new Fraction(100).multiply(
        totalBorrow.divide(totalAssets),
      )
      return { raw: utilisation.numerator, scaled: +utilisation.toFixed(2) }
    }

    return { raw: 0n, scaled: 0 }
  },
)

export const selectPrice = createSelector(
  [
    (amountIn: bigint) => amountIn,
    (_, vault: GetVaultData) => vault,
    (_, __, price: bigint) => price,
  ],
  (amountIn, vault, price): FormattedAssetValue => {
    if (!vault || !price || !vault?.liabilityPriceInfo)
      return { raw: 0n, scaled: 0, usdRaw: 0n, usdScaled: 0 }

    // Use backup pricing if liability pricing query failed
    const liabilityPricing = vault.liabilityPriceInfo.queryFailure
      ? vault.backupAssetPriceInfo
      : vault.liabilityPriceInfo

    if (!liabilityPricing.amountIn || !liabilityPricing.amountOutBid)
      return { raw: 0n, scaled: 0, usdRaw: 0n, usdScaled: 0 }

    const unitOfAccount = new Token({
      decimals: vault.unitOfAccountDecimals.toString(),
      name: vault.unitOfAccountName,
      address: vault.unitOfAccount,
      symbol: vault.unitOfAccountSymbol,
    })

    const asset = new Token({
      decimals: vault.assetDecimals.toString(),
      name: vault.assetName,
      address: vault.asset,
      symbol: vault.assetSymbol,
    })

    // Price of unit of account in USD
    const oraclePrice = new Price(
      unitOfAccount,
      USD_TOKEN,
      10n ** vault.unitOfAccountDecimals,
      price,
    )

    // Price of asset in terms of unit of account
    const tokenPrice = new Price(
      asset,
      unitOfAccount,
      liabilityPricing.amountIn,
      liabilityPricing.amountOutMid,
    )

    const quote = oraclePrice.quote(
      tokenPrice.quote(Amount.fromRawAmount(asset, amountIn)),
    )

    return {
      raw: amountIn,
      scaled: +formatUnits(amountIn, Number(vault.assetDecimals)),
      usdRaw: quote.quotient,
      usdScaled: +quote.toSignificant(quote.currency.decimals),
    }
  },
)

export const selectAvailableToBorrow = createSelector(
  (vault: GetVaultData) => vault,
  (vault) => {
    const totalCash = vault?.totalCash || 0n
    const totalBorrowed = vault?.totalBorrowed || 0n
    const borrowCap =
      vault?.borrowCap === UINT256_MAX ? null : vault?.borrowCap || 0n

    if (!borrowCap) return totalCash

    return BigIntMath.min(
      totalCash,
      borrowCap > totalBorrowed ? borrowCap - totalBorrowed : 0n,
    )
  },
)
export const selectExchangeRate = createSelector(
  [(vault: GetVaultData) => vault],
  (vault) => {
    if (!vault?.totalShares || !vault?.totalAssets) {
      // Default Share Token Exchange Rate is 1.00
      return { raw: 1n * 10n ** (vault?.assetDecimals || 18n), scaled: 1 }
    }

    const exchangeRate = new Fraction(vault.totalAssets, vault.totalShares)

    return {
      raw: exchangeRate.quotient,
      scaled: +exchangeRate.toFixed(Number(vault.assetDecimals)),
    }
  },
)

export const selectApy = createSelector(
  (apy?: bigint) => apy,
  (apy = 0n): FormattedValue => {
    const apyPercent = new Percent(apy, 10n ** 27n)
    return {
      raw: apy,
      scaled: +apyPercent.toFixed(3),
    }
  },
)

export const selectVaultType = createSelector(
  [
    (address: Address) => getAddress(address),
    (_, perspectives: Map<Exclude<VaultType, VaultType.Unknown>, Address[]>) =>
      perspectives,
  ],
  (address, perspectives): VaultType => {
    if (
      perspectives
        .get(VaultType.Custom)
        ?.some((vault) => getAddress(vault) === getAddress(address))
    ) {
      return VaultType.Custom
    }
    if (
      perspectives
        .get(VaultType.Escrow)
        ?.some((vault) => getAddress(vault) === getAddress(address))
    ) {
      return VaultType.Escrow
    }
    if (
      perspectives
        .get(VaultType.Governed)
        ?.some((vault) => getAddress(vault) === getAddress(address))
    ) {
      return VaultType.Governed
    }
    if (
      perspectives
        .get(VaultType.EarnFactory)
        ?.some((vault) => getAddress(vault) === getAddress(address))
    ) {
      return VaultType.EarnFactory
    }

    return VaultType.Unknown
  },
)

export const selectCollateralLtv = createSelector(
  [
    (vault: GetVaultData) => vault?.collateralLTVInfo,
    (_, collateral: Address) => collateral,
  ],
  (ltvInfos, collateral): Pick<Collateral, "borrowLtv" | "liquidationLtv"> => {
    const ltvInfo = ltvInfos?.find(
      (ltv) => getAddress(ltv.collateral) === getAddress(collateral),
    )

    if (!ltvInfo)
      return {
        borrowLtv: {
          raw: 0n,
          scaled: 0,
        },
        liquidationLtv: {
          raw: 0n,
          scaled: 0,
        },
      }

    return {
      borrowLtv: {
        raw: ltvInfo.borrowLTV,
        scaled: +new Percent(ltvInfo.borrowLTV, 10_000).toFixed(2),
      },
      liquidationLtv: {
        raw: ltvInfo.liquidationLTV,
        scaled: +new Percent(ltvInfo.liquidationLTV, 10_000).toFixed(2),
      },
    }
  },
)

export const selectCollateralLTVInfo = createSelector(
  [
    (vault: GetVaultData) => vault?.collateralLTVInfo,
    (_, collateralAddress?: Address) => collateralAddress,
  ],
  (collateralLTVInfo, collateralAddress) => {
    return (
      collateralLTVInfo?.find(
        (info) => info.collateral === collateralAddress,
      ) || null
    )
  },
)
export const selectBorrowingLTVBigInt = createSelector(
  [selectCollateralLTVInfo],
  (collateralLTVInfo) => collateralLTVInfo?.borrowLTV || 0n,
)
export const selectMaxLeverage = createSelector(
  [
    (data, vaultAAddress, _) => selectBorrowingLTVBigInt(data, vaultAAddress),
    (data, _, vaultBAddress) => selectBorrowingLTVBigInt(data, vaultBAddress),
  ],
  (vaultALTV, vaultBLTV): FormattedValue => {
    const scale = LTV_DEFAULT_CALCULATION_SCALE
    const one = 10_000n
    const quotient = one * BigInt(vaultALTV)
    const denominator = one - BigInt(vaultBLTV)
    const result = quotient / denominator - 200n + one
    return { raw: result, scaled: Number(result) / Number(scale) }
  },
)

export const selectCollaterals = createSelector(
  [
    (vault: NonNullable<GetVaultData>) => vault,
    (_, vaultMap: Map<Address, GetVaultData>) => vaultMap,
  ],
  (vault, vaultMap): Collateral[] => {
    const collaterals: Collateral[] = []
    for (const _collateral of vault.collateralLTVInfo) {
      const collateral = vaultMap.get(getAddress(_collateral.collateral))
      if (!collateral) continue

      const { borrowLtv, liquidationLtv } = selectCollateralLtv(
        vault,
        collateral.vault,
      )
      const maxLeverage = selectMaxLeverage(
        vault,
        collateral.vault,
        collateral.vault,
      )
      const asset = selectAsset(collateral)
      const unitOfAccount = selectUnitOfAccount(collateral)
      collaterals.push({
        address: collateral.vault,
        asset,
        unitOfAccount,
        liquidationLtv,
        borrowLtv,
        maxLeverage,
      })
    }

    return collaterals
  },
)

const YEAR = 31_536_000

export function calculateApy(
  epochsInYear: number,
  rewardAmount: number,
  price: number,
  totalRewardedEligible: number,
  rewardedPrice: number,
) {
  if (!totalRewardedEligible) return 0
  return (
    (100 * epochsInYear * rewardAmount * price) /
    (totalRewardedEligible * rewardedPrice)
  )
}

export const selectRewardApy = createSelector(
  [
    (vaultInfo: GetVaultData) => vaultInfo,
    (_, uniOfAccountPrice: bigint) => uniOfAccountPrice,
    (_, __, rewardInfo: GetRewardVaultInfo) => rewardInfo,
  ],
  (vaultInfo, uniOfAccountPrice, rewardInfo): undefined | Reward => {
    if (
      !vaultInfo ||
      !uniOfAccountPrice ||
      !rewardInfo ||
      !rewardInfo.rewardAmount ||
      !rewardInfo.totalRewardedEligible ||
      !rewardInfo.rewardPrice
    )
      return

    const rewardedPriceBn =
      selectPrice(
        parseUnits("1", Number(vaultInfo.assetDecimals)),
        vaultInfo,
        uniOfAccountPrice,
      )?.usdRaw || 0n

    const rewardedPrice = +formatUnits(rewardedPriceBn, 8)

    const asset: Asset = {
      address: rewardInfo.reward,
      decimals: BigInt(rewardInfo.rewardDecimals),
      name: rewardInfo.rewardName,
      symbol: rewardInfo.rewardSymbol,
      image: rewardInfo.rewardLogo,
    }

    if (!rewardedPrice)
      return {
        active: false,
        apy: 0,
        asset,
        nextApy: 0,
        nextRewardAmount: {
          raw: 0n,
          scaled: 0,
          usdRaw: 0n,
          usdScaled: 0,
        },
        price: {
          raw: 0n,
          scaled: 0,
          usdRaw: 0n,
          usdScaled: 0,
        },
        rewardAmount: {
          raw: 0n,
          scaled: 0,
          usdRaw: 0n,
          usdScaled: 0,
        },
      }

    const epochsInYear = YEAR / Number(rewardInfo.epochDuration)

    const rewardAmount = +formatUnits(
      BigInt(rewardInfo.rewardAmount),
      rewardInfo.rewardDecimals,
    )

    const totalRewardedEligible = +formatUnits(
      BigInt(rewardInfo.totalRewardedEligible),
      Number(vaultInfo.assetDecimals),
    )

    const apy = calculateApy(
      epochsInYear,
      rewardAmount,
      rewardInfo.rewardPrice,
      totalRewardedEligible,
      rewardedPrice,
    )

    const curRewardPrice = rewardAmount * rewardInfo.rewardPrice

    const price = {
      raw: 10n ** BigInt(rewardInfo.rewardDecimals),
      scaled: 1,
      usdRaw: parseUnits(rewardInfo.rewardPrice.toString(), 8),
      usdScaled: rewardInfo.rewardPrice,
    }

    const _rewardAmount = {
      raw: BigInt(rewardInfo.rewardAmount || "0"),
      scaled: rewardAmount,
      usdRaw: parseUnits(curRewardPrice.toString(), 8),
      usdScaled: curRewardPrice,
    }

    const active = rewardInfo.currentEpoch <= rewardInfo.lastEpoch

    if (!rewardInfo.nextRewardAmount)
      return {
        apy,
        asset,
        nextApy: 0,
        active,
        price,
        rewardAmount: _rewardAmount,
        nextRewardAmount: {
          raw: 0n,
          scaled: 0,
          usdRaw: 0n,
          usdScaled: 0,
        },
      }

    const nextRewardAmount = +formatUnits(
      BigInt(rewardInfo.nextRewardAmount),
      rewardInfo.rewardDecimals,
    )

    const nextApy = calculateApy(
      epochsInYear,
      nextRewardAmount,
      rewardInfo.rewardPrice,
      totalRewardedEligible,
      rewardedPrice,
    )

    const nextRewardPrice = nextRewardAmount * rewardInfo.rewardPrice

    const _nextRewardAmount = {
      raw: BigInt(rewardInfo.nextRewardAmount || "0"),
      scaled: nextRewardAmount,
      usdRaw: parseUnits(nextRewardPrice.toString(), 8),
      usdScaled: nextRewardPrice,
    }

    return {
      apy,
      asset,
      nextApy,
      active,
      price,
      rewardAmount: _rewardAmount,
      nextRewardAmount: _nextRewardAmount,
    }
  },
)

export const selectRewardsApy = createSelector(
  [
    (vaultInfo: GetVaultData) => vaultInfo,
    (_, uniOfAccountPrice: bigint) => uniOfAccountPrice,
    (_, __, rewards?: Map<Address, Map<Address, GetRewardVaultInfo>>) =>
      rewards,
  ],
  (vaultInfo, uniOfAccountPrice, rewards) => {
    if (!rewards || !vaultInfo || !uniOfAccountPrice) return []
    const vaultRewards = rewards.get(getAddress(vaultInfo.vault))
    if (!vaultRewards) return []
    const rewardInfos = new Map<Address, Reward>()
    for (const [reward, rewardInfo] of vaultRewards.entries()) {
      const apy = selectRewardApy(vaultInfo, uniOfAccountPrice, rewardInfo)
      if (apy) {
        rewardInfos.set(reward, apy)
      }
    }

    return Array.from(rewardInfos.values())
  },
)

export const selectPoints = createSelector(
  [
    (expMarkets: ExperienceMarket[]) => expMarkets,
    (_: ExperienceMarket[], collateral: Address) => collateral,
    (_: ExperienceMarket[], __: Address, liability?: Address) => liability,
  ],
  (expMarkets, collateral, liability) => {
    return [
      ...new Map(
        expMarkets
          .filter(
            ({ collateralVaults = [], liabilityVaults = [] }) =>
              collateralVaults.includes(collateral) ||
              (liability && liabilityVaults.includes(liability)),
          )
          .map((market) => [market.name, market]),
      ).values(),
    ]
  },
)

export const selectVaultData = createSelector(
  [
    (vault: GetVaultData) => vault,
    (_vault: GetVaultData, vaultMap: Map<Address, GetVaultData>) => vaultMap,
    (
      _vault: GetVaultData,
      _vaultMap: Map<Address, GetVaultData>,
      chainId: SupportedChainId,
    ) => chainId,
    (
      _vault: GetVaultData,
      _vaultMap: Map<Address, GetVaultData>,
      _chainId: SupportedChainId,
      unitOfAccountPriceMap: Map<Address, bigint>,
    ) => unitOfAccountPriceMap,
    (
      _vault: GetVaultData,
      _vaultMap: Map<Address, GetVaultData>,
      _chainId: SupportedChainId,
      _unitOfAccountPriceMap: Map<Address, bigint>,
      perspectives: Map<
        Exclude<VaultType, VaultType.Unknown | VaultType.Factory>,
        Address[]
      >,
    ) => perspectives,
    (
      _vault: GetVaultData,
      _vaultMap: Map<Address, GetVaultData>,
      _chainId: SupportedChainId,
      _unitOfAccountPriceMap: Map<Address, bigint>,
      _perspectives: Map<
        Exclude<VaultType, VaultType.Unknown | VaultType.Factory>,
        Address[]
      >,
      balance?: bigint,
    ) => balance,
    (
      _vault: GetVaultData,
      _vaultMap: Map<Address, GetVaultData>,
      _chainId: SupportedChainId,
      _unitOfAccountPriceMap: Map<Address, bigint>,
      _perspectives: Map<
        Exclude<VaultType, VaultType.Unknown | VaultType.Factory>,
        Address[]
      >,
      _balance?: bigint,
      rewardMap?: Map<Address, Map<Address, GetRewardVaultInfo>>,
    ) => rewardMap,
    (
      _vault: GetVaultData,
      _vaultMap: Map<Address, GetVaultData>,
      _chainId: SupportedChainId,
      _unitOfAccountPriceMap: Map<Address, bigint>,
      _perspectives: Map<
        Exclude<VaultType, VaultType.Unknown | VaultType.Factory>,
        Address[]
      >,
      _balance?: bigint,
      _rewardMap?: Map<Address, Map<Address, GetRewardVaultInfo>>,
      nativeApys?: Map<Address, number>,
    ) => nativeApys,
    (
      _vault: GetVaultData,
      _vaultMap: Map<Address, GetVaultData>,
      _chainId: SupportedChainId,
      _unitOfAccountPriceMap: Map<Address, bigint>,
      _perspectives: Map<
        Exclude<VaultType, VaultType.Unknown | VaultType.Factory>,
        Address[]
      >,
      _balance?: bigint,
      _rewardMap?: Map<Address, Map<Address, GetRewardVaultInfo>>,
      _nativeApys?: Map<Address, number>,
      expMarkets?: ExperienceMarket[],
    ) => expMarkets,
  ],
  (
    vault,
    vaultMap,
    chainId,
    unitOfAccountPriceMap,
    perspectives,
    balance,
    rewardMap,
    nativeApys,
    expMarkets,
  ): Vault | null => {
    if (!vault) return null
    const asset = selectAsset(vault)
    const unitOfAccount = selectUnitOfAccount(vault)

    const unitOfAccountPrice =
      unitOfAccountPriceMap.get(unitOfAccount.address) || 0n
    const label = getVaultLabels({
      chainId,
    })?.get(getAddress(vault.vault))

    const vaultCreator = getEntities({
      chainId,
    })?.get(
      (Array.isArray(label?.entity) ? label?.entity[0] : label?.entity) || "",
    )

    const availableToBorrow = selectAvailableToBorrow(vault)

    return {
      address: vault.vault,
      asset,
      customName: label?.name,
      name: vault.vaultName,
      symbol: vault.vaultSymbol,
      vaultCreator: vaultCreator,
      unitOfAccount,
      vaultType: selectVaultType(vault.vault, perspectives),
      /// values
      availableToBorrow: selectPrice(
        availableToBorrow,
        vault,
        unitOfAccountPrice,
      ),
      borrowApy: selectApy(vault.irmInfo.interestRateInfo[0]?.borrowAPY),
      exchangeRate: selectExchangeRate(vault),
      price: selectPrice(
        parseUnits("1", Number(asset.decimals)),
        vault,
        unitOfAccountPrice,
      ),
      supplyApy: selectApy(vault.irmInfo.interestRateInfo[0]?.supplyAPY),
      totalAssets: selectPrice(vault.totalAssets, vault, unitOfAccountPrice),
      totalBorrowed: selectPrice(
        vault.totalBorrowed,
        vault,
        unitOfAccountPrice,
      ),
      totalCash: selectPrice(vault.totalCash, vault, unitOfAccountPrice),
      totalShares: selectPrice(vault.totalShares, vault, unitOfAccountPrice),
      utilisation: selectUtilization(vault),
      walletBalance: balance
        ? selectPrice(balance, vault, unitOfAccountPrice)
        : undefined,
      /// parallel values
      collaterals: selectCollaterals(vault, vaultMap),
      rewards: selectRewardsApy(vault, unitOfAccountPrice, rewardMap),
      nativeApy: nativeApys?.get(getAddress(vault.asset)),
      points: selectPoints(expMarkets || [], vault.asset),
      raw: vault,
    }
  },
)
